/**
 * Session hand-off.
 *
 * A buyer who is already signed in in the Cuanto app taps a store from Buscar
 * and arrives here with a one-shot token in the URL. The store itself does
 * nothing with it: it only keeps it for the visit and hands it to the checkout,
 * which trades it for a consumer session so the buyer never retypes their
 * email, address or card.
 *
 * See thoughts/shared/plans/quiero-comprar-v2.md (deliverable B).
 */
export const HANDOFF_PARAM = "mc";

const STORAGE_KEY = "cuantoHandoffToken";

/** Pure. The hand-off token carried by a query string, if there is one. */
export function handoffTokenFromSearch(search: string): string | null {
  try {
    return new URLSearchParams(search).get(HANDOFF_PARAM) || null;
  } catch {
    return null;
  }
}

/**
 * Pure. `url` with the hand-off token appended, or exactly `url` when there is
 * no token to append. Respects a query string the URL already carries.
 */
export function withHandoff(url: string, token: string | null): string {
  if (!token) return url;

  const separator = url.indexOf("?") === -1 ? "?" : "&";
  return `${url}${separator}${HANDOFF_PARAM}=${encodeURIComponent(token)}`;
}

/**
 * Pure. `search` without the hand-off parameter, as a query string ready to
 * put back on the URL ("" when nothing is left).
 */
export function searchWithoutHandoff(search: string): string {
  try {
    const params = new URLSearchParams(search);
    params.delete(HANDOFF_PARAM);
    const rest = params.toString();
    return rest ? `?${rest}` : "";
  } catch {
    return search;
  }
}

/**
 * Remembers the token for the rest of this browsing session, then takes it out
 * of the address bar. The buyer usually browses before checking out, and the
 * landing URL is long gone by then.
 *
 * Erasing it matters: this page opens inside the app's in-app browser, where
 * the URL is visible and shareable, and the token is a live session for half
 * an hour. The checkout does the same on its side.
 */
export function captureHandoffToken(search: string): void {
  const token = handoffTokenFromSearch(search);
  if (!token) return;

  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Session storage unavailable (private mode, blocked cookies). The buyer
    // signs in at the checkout as they always have.
  }

  try {
    const { pathname, hash } = window.location;
    window.history.replaceState(
      null,
      "",
      `${pathname}${searchWithoutHandoff(search)}${hash}`
    );
  } catch {
    // No History API (a sandboxed frame). The token stays in the bar; it is
    // still single-use and short-lived.
  }
}

function storedHandoffToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** The checkout URL to send this buyer to, carrying the hand-off if we have one. */
export function checkoutUrlWithHandoff(url: string): string {
  return withHandoff(url, storedHandoffToken());
}
